import type { ReadonlyVec3 } from 'gl-matrix';
import {
    PlaneFrame,
    planeFrameOf,
    planesAreCoincident,
    pointSegmentDistance,
    projectToPlane,
    toPointMm,
    Vec2Mm,
} from '~/domain/geometry/patientGeometry';
import { isPointInsidePolygon, ringNesting } from '../polygonWinding';
import { CrossSectionError, CrossSectionPlane } from './crossSectionTypes';

/** Within these, a drawn plane and a displayed slice are the same slice: contours on it are shown and extended there, and a line stays on it. */
const SAME_SLICE_TOLERANCE_DEG = 0.5;
const SAME_SLICE_TOLERANCE_MM = 0.5;
const DUPLICATE_VERTEX_EPSILON_MM = 1e-9;

/** {@link planeFrameOf} for a drawn plane; @throws CrossSectionError `invalid-parameter` for a degenerate plane. */
export function buildPlaneFrame(plane: CrossSectionPlane): PlaneFrame {
    const frame = planeFrameOf({ point: plane.originMm, normal: plane.normal });
    if (!frame) {
        throw new CrossSectionError(
            'invalid-parameter',
            `plane needs a finite origin and a non-degenerate normal, got origin [${[...plane.originMm].join(', ')}], normal [${[...plane.normal].join(', ')}]`
        );
    }
    return frame;
}

/** Whether two drawn planes are the same slice to the drawing tools; @throws CrossSectionError `invalid-parameter` for a degenerate plane. */
export function crossSectionPlanesShareSlice(
    a: CrossSectionPlane,
    b: CrossSectionPlane
): boolean {
    return planesAreCoincident(
        buildPlaneFrame(a),
        buildPlaneFrame(b),
        SAME_SLICE_TOLERANCE_DEG,
        SAME_SLICE_TOLERANCE_MM
    );
}

function isWithinVertexEpsilon(a: Vec2Mm, b: Vec2Mm): boolean {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const eps = DUPLICATE_VERTEX_EPSILON_MM;
    return dx * dx + dy * dy < eps * eps;
}

/** The polygon without consecutive duplicate vertices or a repeated closing vertex. */
export function canonicalisePolygon(
    polygon: readonly Vec2Mm[]
): readonly Vec2Mm[] {
    const out: Vec2Mm[] = [];
    for (const vertex of polygon) {
        const last = out[out.length - 1];
        if (!last || !isWithinVertexEpsilon(vertex, last)) {
            out.push(vertex);
        }
    }
    while (
        out.length > 1 &&
        isWithinVertexEpsilon(out[0], out[out.length - 1])
    ) {
        out.pop();
    }
    return out;
}

/** Signed distance to the union of canonical rings: negative inside any of them. */
export function polygonSetSdf2d(
    polygons: readonly (readonly Vec2Mm[])[],
    p: Vec2Mm
): number {
    let best = Infinity;
    for (const ring of polygons) {
        let edge = Infinity;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            edge = Math.min(edge, pointSegmentDistance(p, ring[j], ring[i]));
        }
        best = Math.min(best, isPointInsidePolygon(p, ring) ? -edge : edge);
    }
    return best;
}

/** `inside` when a contour lies within another on its plane, `around` when it encloses it. */
export type ContourNesting = 'inside' | 'around';

/** How `contourMm` nests with `othersMm` on `plane` as `ringNesting` defines it, `inside` first; @throws CrossSectionError `invalid-parameter` for a degenerate plane. */
export function contourNestingOnPlane(
    plane: CrossSectionPlane,
    contourMm: readonly ReadonlyVec3[],
    othersMm: readonly (readonly ReadonlyVec3[])[]
): ContourNesting | null {
    const frame = buildPlaneFrame(plane);
    const inPlane = (contour: readonly ReadonlyVec3[]): Vec2Mm[] =>
        contour.map((p) => projectToPlane(frame, toPointMm(p)));
    const ring = inPlane(contourMm);
    let around = false;
    for (const other of othersMm) {
        const { depth } = ringNesting([ring, inPlane(other)]);
        if (depth[0] > 0) {
            return 'inside';
        }
        around ||= depth[1] > 0;
    }
    return around ? 'around' : null;
}

/** A vertex where a ring turns: its arc length and signed turn in radians (−π to π). */
export interface RingCorner {
    readonly arcMm: number;
    readonly turnRad: number;
}

export interface ClosedCurve2d {
    readonly perimeterMm: number;
    /** The point at arc length `t`, wrapping around the ring. */
    at(t: number): Vec2Mm;
    /** The vertices where the ring turns by more than `maxTurnDeg`, in ring order. */
    corners(maxTurnDeg: number): RingCorner[];
}

export function parameteriseClosed(polygon: readonly Vec2Mm[]): ClosedCurve2d {
    const ring = canonicalisePolygon(polygon);
    const n = ring.length;
    if (n < 3) {
        throw new CrossSectionError(
            'too-few-vertices',
            `a cross-section polygon needs at least 3 distinct vertices, got ${n}`
        );
    }
    const cumulative = new Float64Array(n + 1);
    for (let i = 0; i < n; i++) {
        const [a, b] = [ring[i], ring[(i + 1) % n]];
        const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
        cumulative[i + 1] = cumulative[i] + Math.sqrt(dx * dx + dy * dy);
    }
    const perimeterMm = cumulative[n];
    return {
        perimeterMm,
        at(t: number): Vec2Mm {
            let s = t % perimeterMm;
            s = s < 0 ? s + perimeterMm : s;
            let [lo, hi] = [0, n];
            while (lo + 1 < hi) {
                const mid = (lo + hi) >> 1;
                [lo, hi] = cumulative[mid] <= s ? [mid, hi] : [lo, mid];
            }
            const [a, b] = [ring[lo], ring[(lo + 1) % n]];
            const edge = cumulative[lo + 1] - cumulative[lo];
            const f = edge > 0 ? (s - cumulative[lo]) / edge : 0;
            return [a[0] + f * (b[0] - a[0]), a[1] + f * (b[1] - a[1])];
        },
        corners(maxTurnDeg: number): RingCorner[] {
            const threshold = (maxTurnDeg * Math.PI) / 180;
            const out: RingCorner[] = [];
            for (let i = 0; i < n; i++) {
                const prev = ring[(i - 1 + n) % n];
                const [here, next] = [ring[i], ring[(i + 1) % n]];
                let turn =
                    Math.atan2(next[1] - here[1], next[0] - here[0]) -
                    Math.atan2(here[1] - prev[1], here[0] - prev[0]);
                while (turn > Math.PI) {
                    turn -= 2 * Math.PI;
                }
                while (turn < -Math.PI) {
                    turn += 2 * Math.PI;
                }
                if (Math.abs(turn) > threshold) {
                    out.push({ arcMm: cumulative[i], turnRad: turn });
                }
            }
            return out;
        },
    };
}

/** The ring at max(3, round(perimeter / `spacingMm`)) evenly spaced points from its first vertex; @throws CrossSectionError `invalid-parameter`, `too-few-vertices`. */
export function resampleClosedPolygonUniform(
    polygon: readonly Vec2Mm[],
    spacingMm: number
): Vec2Mm[] {
    if (!Number.isFinite(spacingMm) || spacingMm <= 0) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygonUniform: spacingMm must be positive, got ${spacingMm}`
        );
    }
    const curve = parameteriseClosed(polygon);
    const count = Math.max(3, Math.round(curve.perimeterMm / spacingMm));
    const step = curve.perimeterMm / count;
    return Array.from({ length: count }, (_, i) => curve.at(i * step));
}

/**
 * At most `maxSamples` points in ring order: the sharpest corners that fit the budget, then `sampleCount` even samples yielding to corners within a quarter step.
 * @throws CrossSectionError `invalid-parameter` for a bad count, `too-few-vertices` for fewer than 3 vertices.
 */
export function resampleClosedPolygon(
    polygon: readonly Vec2Mm[],
    sampleCount: number,
    maxTurnDeg: number,
    maxSamples: number
): Vec2Mm[] {
    if (!Number.isFinite(sampleCount) || sampleCount < 3) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygon: sampleCount must be at least 3, got ${sampleCount}`
        );
    }
    if (!(maxSamples >= sampleCount)) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygon: maxSamples (${maxSamples}) must be at least sampleCount (${sampleCount})`
        );
    }
    const curve = parameteriseClosed(polygon);
    const step = curve.perimeterMm / sampleCount;
    const kept = curve
        .corners(maxTurnDeg)
        .sort(
            (a, b) =>
                Math.abs(b.turnRad) - Math.abs(a.turnRad) || a.arcMm - b.arcMm
        )
        .slice(0, Math.floor(maxSamples - sampleCount))
        .map((corner) => corner.arcMm);
    for (let i = 0; i < sampleCount; i++) {
        const t = i * step;
        const clash = kept.some((k) => {
            const d = Math.abs(k - t);
            return Math.min(d, curve.perimeterMm - d) < step / 4;
        });
        if (!clash) {
            kept.push(t);
        }
    }
    return kept.sort((a, b) => a - b).map((t) => curve.at(t));
}
